import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslation } from '../../translations/translations';
import { createEvent, editEvent, deleteEvent, listEvents } from '../../Fetch/fetchEvent';
import EventForm from './EventForm';
import styles from './Event.module.css';
import { sendInvitations } from '../../Fetch/fetchSendInvitations';
import { getEventsWithApprovalCounts, syncAllEventApprovalCounts } from '../../Fetch/fetchApprovalCounts';
import { getUserData } from '../../utils/getUserData';
import {
    Search,
    ChevronLeft,
    ChevronRight,
    Edit,
    Trash,
    Plus,
    Calendar as CalendarIcon,
    Users as UsersIcon,
    CheckCircle,
    Clock,
    Filter,
    RefreshCw,
    Zap,
    ChevronDown,
    MapPin,
    Phone,
    Mail
} from 'lucide-react';

// Action Menu Modal Component
const ActionMenu = ({ event, onEdit, onDelete, onWhatsAppShare, onSendInvitations, loading }) => {
    const [isOpen, setIsOpen] = useState(false);

    const actions = [
        {
            label: '📱 שתף WhatsApp',
            onClick: () => onWhatsAppShare(event),
            disabled: loading
        },
        {
            label: '📧 שלח הזמנות',
            onClick: () => onSendInvitations(event),
            disabled: loading
        },
        {
            label: '✏️ ערוך',
            onClick: () => onEdit(event),
            disabled: loading
        },
        {
            label: '🗑️ מחק',
            onClick: () => onDelete(event.id),
            disabled: loading,
            isDanger: true
        }
    ];

    const handleActionClick = (action) => {
        if (!action.disabled) {
            action.onClick();
            setIsOpen(false);
        }
    };

    return (
        <>
            <button
                className={`${styles.actionMenuTrigger} ${loading ? styles.disabled : ''}`}
                onClick={() => setIsOpen(true)}
                disabled={loading}
                aria-label="פתח תפריט פעולות"
            >
                <span>⋯</span>
            </button>

            {isOpen && createPortal(
                <div className={styles.modalOverlay} onClick={() => setIsOpen(false)}>
                    <div className={styles.actionMenuModal} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.actionMenuHeader}>
                            <h3>פעולות</h3>
                            <button
                                className={styles.closeButton}
                                onClick={() => setIsOpen(false)}
                                aria-label="סגור"
                            >
                                ✕
                            </button>
                        </div>
                        <div className={styles.actionMenuContent}>
                            {actions.map((action, index) => (
                                <button
                                    key={index}
                                    className={`${styles.actionMenuItem} ${action.isDanger ? styles.dangerAction : ''} ${action.disabled ? styles.disabled : ''}`}
                                    onClick={() => handleActionClick(action)}
                                    disabled={action.disabled}
                                >
                                    {action.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

// EventCard Component for Mobile View
const EventCard = ({ event, onEdit, onDelete, onWhatsAppShare, onSendInvitations, getStatusClass, loading }) => {
    return (
        <div className={styles.dataCard}>
            <div className={styles.cardHeader}>
                <div className={styles.cardTitle}>
                    <CalendarIcon size={16} className={styles.cardIcon} />
                    <span>{event.name}</span>
                </div>
                <div className={styles.cardHeaderActions}>
                    <span className={`${styles.statusBadge} ${getStatusClass(event.status)}`}>
                        {event.status}
                    </span>
                    <ActionMenu
                        event={event}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onWhatsAppShare={onWhatsAppShare}
                        onSendInvitations={onSendInvitations}
                        loading={loading}
                    />
                </div>
            </div>
            <div className={styles.cardBody}>
                <div className={styles.cardInfo}>
                    <CalendarIcon size={14} />
                    <span>{event.date} • {event.time}</span>
                </div>
                <div className={styles.cardInfo}>
                    <MapPin size={14} />
                    <span>{event.location}</span>
                </div>
                <div className={styles.cardInfo}>
                    <UsersIcon size={14} />
                    <span>
                        מאשרים: {event.confirmUsersNumber}
                        {event.actualApprovalCount !== undefined &&
                            event.actualApprovalCount !== event.confirmUsersNumber && (
                                <span className={styles.countMismatch}>
                                    (בפועל: {event.actualApprovalCount})
                                </span>
                            )}
                    </span>
                </div>
                {event.value && (
                    <div className={styles.cardInfo}>
                        <span>💰</span>
                        <span>פרטי אירוע: {event.value}</span>
                    </div>
                )}
                <div className={styles.cardInfo}>
                    <span>👤</span>
                    <span>נוצר על ידי: {event.createdBy}</span>
                </div>
            </div>
        </div>
    );
};

const Event = () => {
    const location = useLocation();
    const { language } = useLanguage();
    const { t } = useTranslation(language);

    const [events, setEvents] = useState([]);
    const [filteredEvents, setFilteredEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [buildingId, setBuildingId] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [eventsPerPage, setEventsPerPage] = useState(10);
    const [activeTab, setActiveTab] = useState('all');

    // Statistics
    const totalEvents = events.length;
    const confirmedEvents = events.filter(e => e.status === 'מאושר').length;
    const pendingEvents = events.filter(e => e.status === 'ממתין').length;
    const completedEvents = events.filter(e => e.status === 'הסתיים').length;

    // Handle navigation from Dashboard
    useEffect(() => {
        const navigationEvent = location.state?.editingEvent;
        const fromDashboard = location.state?.fromDashboard;

        if (navigationEvent && fromDashboard) {

            const openEditForm = () => {
                const eventForEdit = {
                    id: navigationEvent.id,
                    name: navigationEvent.name || '',
                    status: navigationEvent.status || 'ממתין',
                    confirmUsersNumber: navigationEvent.confirmUsersNumber || 0,
                    value: navigationEvent.value || navigationEvent.name || 'ישיבת וועד',
                    date: navigationEvent.date || '',
                    time: navigationEvent.time || '',
                    location: navigationEvent.location || '',
                    createdBy: navigationEvent.createdBy || '',
                    buildingId: navigationEvent.buildingId || buildingId,
                    TopicsDiscussed: navigationEvent.TopicsDiscussed || navigationEvent.topicsDiscussed || '',
                    Decisions: navigationEvent.Decisions || navigationEvent.decisions || '',
                    topicsDiscussed: navigationEvent.topicsDiscussed || navigationEvent.TopicsDiscussed || '',
                    decisions: navigationEvent.decisions || navigationEvent.Decisions || ''
                };

                setEditingEvent(eventForEdit);
                setShowForm(true);
            };

            if (events.length > 0) {
                openEditForm();
            } else {
                const timeoutId = setTimeout(() => {
                    openEditForm();
                }, 1000);

                return () => {
                    clearTimeout(timeoutId);
                };
            }

            window.history.replaceState({}, document.title);
        }
    }, [location.state, events.length, buildingId]);

    // Load buildingId and events on component mount
    useEffect(() => {
        try {
            // Use the getUserData utility which handles both localStorage and sessionStorage
            const user = getUserData();

            if (user?.buildingId) {
                setBuildingId(user.buildingId);
            } else {
            }
        } catch (error) {
        }
    }, []);

    // Add event listener for building changes
    useEffect(() => {
        const handleBuildingChange = () => {
            const userData = getUserData();
            const newBuildingId = userData?.buildingId;

            if (newBuildingId && newBuildingId !== buildingId) {
                setBuildingId(newBuildingId);
                loadEvents(newBuildingId);
            }
        };

        window.addEventListener('buildingChanged', handleBuildingChange);
        window.addEventListener('userDataChanged', handleBuildingChange);

        return () => {
            window.removeEventListener('buildingChanged', handleBuildingChange);
            window.removeEventListener('userDataChanged', handleBuildingChange);
        };
    }, [buildingId]);

    // Load events when buildingId changes
    useEffect(() => {
        if (buildingId) {
            loadEvents(buildingId);
        }
    }, [buildingId]);

    // Filter events based on search term, status, and tab
    useEffect(() => {
        let filtered = events;

        // Filter by tab
        if (activeTab === 'confirmed') {
            filtered = filtered.filter(e => e.status === 'מאושר');
        } else if (activeTab === 'pending') {
            filtered = filtered.filter(e => e.status === 'ממתין');
        } else if (activeTab === 'completed') {
            filtered = filtered.filter(e => e.status === 'הסתיים');
        }

        // Filter by status
        if (statusFilter) {
            filtered = filtered.filter(event => event.status === statusFilter);
        }

        // Filter by search term
        if (searchTerm) {
            filtered = filtered.filter(event =>
                event.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                event.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
                event.createdBy.toLowerCase().includes(searchTerm.toLowerCase()) ||
                event.status.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        setFilteredEvents(filtered);
        setCurrentPage(1);
    }, [events, searchTerm, statusFilter, activeTab]);

    // Get unique status values for filter
    const uniqueStatuses = React.useMemo(() => {
        const statuses = events
            .map(e => e.status)
            .filter((status, index, arr) => arr.indexOf(status) === index);
        return statuses;
    }, [events]);

    // Clear filters function
    const clearFilters = () => {
        setSearchTerm('');
        setStatusFilter('');
    };

    const loadEvents = async (buildingIdToUse = buildingId) => {
        if (!buildingIdToUse) {
            return;
        }

        setLoading(true);
        try {
            const eventList = await listEvents(buildingIdToUse);
            setEvents(eventList);
        } catch (error) {
            alert('Failed to load events');
        } finally {
            setLoading(false);
        }
    };

    const refreshApprovalCounts = async () => {
        if (!buildingId) return;

        setLoading(true);
        try {
            const result = await getEventsWithApprovalCounts(parseInt(buildingId));

            if (result.success) {
                setEvents(prevEvents =>
                    prevEvents.map(existingEvent => {
                        const updatedEventData = result.events.find(
                            apiEvent => apiEvent.id === existingEvent.id
                        );

                        if (updatedEventData) {
                            return {
                                ...existingEvent,
                                confirmUsersNumber: updatedEventData.actualApprovalCount,
                                actualApprovalCount: updatedEventData.actualApprovalCount,
                                approvalUsers: updatedEventData.approvalUsers
                            };
                        }

                        return existingEvent;
                    })
                );
            }
        } catch (error) {
        } finally {
            setLoading(false);
        }
    };


    const handleWhatsAppShare = (event) => {
        const apiUrl = process.env.REACT_APP_API_URL || 'https://localhost:7204';

        // Encrypt the building ID for security (same as email invitations)
        // You'll need to add an encryption function or call an API endpoint to get encrypted buildingId

        // Option A: Call an endpoint to get encrypted buildingId
        const getEncryptedBuildingId = async () => {
            try {
                const response = await fetch(`${apiUrl}/api/Approvers/EncryptBuildingId?buildingId=${buildingId}`);
                const result = await response.json();
                return result.encryptedBuildingId;
            } catch (error) {
                return buildingId; // fallback to plain ID
            }
        };

        // For now, using the phone approval endpoint with plain buildingId
        // but you could modify this to use encrypted ID
        const approvalLink = `${apiUrl}/api/Approvers/ApproveEventWithPhone?buildingId=${encodeURIComponent(buildingId)}&eventName=${encodeURIComponent(event.name)}&dateTime=${encodeURIComponent(event.date + ' ' + event.time)}`;

        const message = `🎉 הזמנה לאירוע: ${event.name}

📅 תאריך: ${event.date}
🕐 שעה: ${event.time}
📍 מקום: ${event.location}
${event.value ? `💰 פרטים: ${event.value}` : ''}

סטטוס: ${event.status}
${event.confirmUsersNumber ? `👥 מספר מאשרים: ${event.confirmUsersNumber}` : ''}

✅ לאישור הגעה לחץ כאן:
${approvalLink}

מוזמנים להצטרף!`;

        const encodedMessage = encodeURIComponent(message);

        // Rest of WhatsApp logic remains the same...
        const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
            (navigator.maxTouchPoints && navigator.maxTouchPoints > 2 && /MacIntel/.test(navigator.platform));

        if (isMobileDevice) {
            const nativeUrl = `whatsapp://send?text=${encodedMessage}`;
            const webUrl = `https://web.whatsapp.com/send?text=${encodedMessage}`;

            const link = document.createElement('a');
            link.href = nativeUrl;

            try {
                link.click();
                setTimeout(() => {
                    if (document.visibilityState === 'visible') {
                        window.open(webUrl, '_blank', 'noopener,noreferrer');
                    }
                }, 2000);
            } catch (error) {
                window.open(webUrl, '_blank', 'noopener,noreferrer');
            }
        } else {
            const whatsappUrl = `https://web.whatsapp.com/send?text=${encodedMessage}`;
            window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
        }
    };


    const handleSyncApprovalCounts = async () => {
        if (!buildingId) return;

        if (window.confirm('Sync approval counts for all events? This will update the database.')) {
            setLoading(true);
            try {
                const result = await syncAllEventApprovalCounts(parseInt(buildingId));

                if (result.success) {
                    alert(`Successfully synced ${result.updatedCount} events out of ${result.totalEvents} total events.`);
                    loadEvents();
                } else {
                    alert(`Failed to sync approval counts: ${result.message}`);
                }
            } catch (error) {
                alert(`Failed to sync approval counts: ${error.message}`);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleAddEvent = () => {
        setEditingEvent(null);
        setShowForm(true);
    };

    const handleEditEvent = (event) => {
        setEditingEvent(event);
        setShowForm(true);
    };

    const handleEventSaved = (savedEvent) => {
        if (editingEvent) {
            setEvents(prev => prev.map(e =>
                e.id === savedEvent.id ? savedEvent : e
            ));
        } else {
            loadEvents();
        }
        setShowForm(false);
        setEditingEvent(null);
    };

    const handleFormCancel = () => {
        setShowForm(false);
        setEditingEvent(null);
    };

    const handleDelete = async (eventId) => {
        if (window.confirm('Are you sure you want to delete this event?')) {
            setLoading(true);
            try {
                await deleteEvent(eventId);
                alert('Event deleted successfully!');
                loadEvents();
            } catch (error) {
                alert('Failed to delete event');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleSendInvitations = async (event) => {
        if (window.confirm(`Are you sure you want to send invitations for "${event.name}" to all users in the building?`)) {
            setLoading(true);
            try {
                const result = await sendInvitations(
                    buildingId,
                    event.name,
                    `${event.date} ${event.time}`
                );

                if (result.success) {
                    alert(`Invitations sent successfully! ${result.sentCount} out of ${result.totalCount} emails were sent.`);
                } else {
                    alert(`Failed to send invitations: ${result.message}`);
                }
            } catch (error) {
                alert(`Failed to send invitations: ${error.message}`);
            } finally {
                setLoading(false);
            }
        }
    };

    const getStatusClass = (status) => {
        switch (status) {
            case 'מאושר': return styles.statusConfirmed;
            case 'מבוטל': return styles.statusCancelled;
            case 'הסתיים': return styles.statusCompleted;
            default: return styles.statusPending;
        }
    };

    // Pagination logic
    const indexOfLastEvent = currentPage * eventsPerPage;
    const indexOfFirstEvent = indexOfLastEvent - eventsPerPage;
    const currentEvents = filteredEvents.slice(indexOfFirstEvent, indexOfLastEvent);
    const totalPages = Math.ceil(filteredEvents.length / eventsPerPage);

    const paginate = (pageNumber) => {
        if (pageNumber > 0 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };


    // Show EventForm if form is active
    if (showForm) {
        return (
            <EventForm
                event={editingEvent}
                onSave={handleEventSaved}
                onCancel={handleFormCancel}
            />
        );
    }

    return (
        <div className={styles.eventsManagement}>
            {/* Header with Stats */}
            <div className={styles.header}>
                <h1>ניהול אירועים</h1>
                <div className={styles.stats}>
                    <div className={styles.statCard}>
                        <CalendarIcon size={24} className={styles.statIcon} />
                        <div>
                            <h3>סה"כ אירועים</h3>
                            <p className={styles.statValue}>{totalEvents}</p>
                        </div>
                    </div>
                    <div className={styles.statCard}>
                        <CheckCircle size={24} className={styles.statIcon} />
                        <div>
                            <h3>מאושרים</h3>
                            <p className={styles.statValue}>{confirmedEvents}</p>
                        </div>
                    </div>
                    <div className={styles.statCard}>
                        <Clock size={24} className={styles.statIcon} />
                        <div>
                            <h3>ממתינים</h3>
                            <p className={styles.statValue}>{pendingEvents}</p>
                        </div>
                    </div>
                    <div className={styles.statCard}>
                        <CheckCircle size={24} className={styles.statIcon} />
                        <div>
                            <h3>הסתיימו</h3>
                            <p className={styles.statValue}>{completedEvents}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className={styles.tabs}>
                <button
                    className={`${styles.tab} ${activeTab === 'all' ? styles.active : ''}`}
                    onClick={() => setActiveTab('all')}
                >
                    <CalendarIcon size={18} />
                    כל האירועים ({totalEvents})
                </button>
                <button
                    className={`${styles.tab} ${activeTab === 'confirmed' ? styles.active : ''}`}
                    onClick={() => setActiveTab('confirmed')}
                >
                    <CheckCircle size={18} />
                    מאושרים ({confirmedEvents})
                </button>
                <button
                    className={`${styles.tab} ${activeTab === 'pending' ? styles.active : ''}`}
                    onClick={() => setActiveTab('pending')}
                >
                    <Clock size={18} />
                    ממתינים ({pendingEvents})
                </button>
                <button
                    className={`${styles.tab} ${activeTab === 'completed' ? styles.active : ''}`}
                    onClick={() => setActiveTab('completed')}
                >
                    <CheckCircle size={18} />
                    הסתיימו ({completedEvents})
                </button>
            </div>

            {/* Actions and Filters */}
            <div className={styles.actions}>
                <div className={styles.filtersContainer}>
                    <div className={styles.searchBar}>
                        <input
                            type="text"
                            placeholder="חיפוש אירועים..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <Search size={20} className={styles.searchIcon} />
                    </div>

                    <div className={styles.statusFilter}>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className={styles.statusSelect}
                        >
                            <option value="">{t('allStatuses')}</option>
                            {uniqueStatuses.map(status => (
                                <option key={status} value={status}>
                                    {status}
                                </option>
                            ))}
                        </select>
                        <Filter size={18} className={styles.filterIcon} />
                    </div>

                    {(searchTerm || statusFilter) && (
                        <button
                            className={styles.clearFiltersButton}
                            onClick={clearFilters}
                            title="נקה מסננים"
                        >
                            <Filter size={18} />
                            נקה
                        </button>
                    )}
                </div>

                <div className={styles.actionButtons}>
                    <button
                        className={`${styles.refreshButton} ${loading ? styles.disabled : ''}`}
                        onClick={refreshApprovalCounts}
                        disabled={loading}
                        title="רענן מספר מאשרים"
                    >
                        <RefreshCw size={20} />
                        רענן מאשרים
                    </button>
                    <button
                        className={`${styles.syncButton} ${loading ? styles.disabled : ''}`}
                        onClick={handleSyncApprovalCounts}
                        disabled={loading}
                        title="סנכרן כל האישורים"
                    >
                        <Zap size={20} />
                        סנכרן הכל
                    </button>
                    <button
                        className={`${styles.addButton} ${loading ? styles.disabled : ''}`}
                        onClick={handleAddEvent}
                        disabled={loading}
                    >
                        <Plus size={20} />
                        הוסף אירוע חדש
                    </button>
                </div>
            </div>

            {loading ? (
                <div className={styles.loadingContainer}>
                    <div className={styles.loadingSpinner}></div>
                    <p>טוען אירועים...</p>
                </div>
            ) : (
                <>
                    {/* Desktop Table View */}
                    <div className={styles.tableContainer}>
                        <table className={styles.eventsTable}>
                            <thead>
                                <tr>
                                    <th>{t('eventName')}</th>
                                    <th>{t('status')}</th>
                                    <th>{t('dateAndTime')}</th>
                                    <th>{t('eventLocation')}</th>
                                    <th>{t('confirmations')}</th>
                                    <th>{t('eventDetails')}</th>
                                    <th>{t('createdBy')}</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentEvents.length > 0 ? (
                                    currentEvents.map((event) => (
                                        <tr key={event.id}>
                                            <td className={styles.eventName}>{event.name}</td>
                                            <td>
                                                <span className={`${styles.statusBadge} ${getStatusClass(event.status)}`}>
                                                    {event.status}
                                                </span>
                                            </td>
                                            <td>
                                                <div className={styles.dateTimeContainer}>
                                                    <span className={styles.date}>{event.date}</span>
                                                    <span className={styles.time}>{event.time}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className={styles.locationContainer}>
                                                    <MapPin size={16} />
                                                    <span>{event.location}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className={styles.participantsContainer}>
                                                    <UsersIcon size={16} />
                                                    <span>{event.confirmUsersNumber}</span>
                                                    {event.actualApprovalCount !== undefined &&
                                                        event.actualApprovalCount !== event.confirmUsersNumber && (
                                                            <span className={styles.countMismatch}>
                                                                (בפועל: {event.actualApprovalCount})
                                                            </span>
                                                        )}
                                                </div>
                                            </td>
                                            <td>
                                                {event.value && (
                                                    <div className={styles.valueContainer}>
                                                        <span>💰</span>
                                                        <span>{event.value}</span>
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <div className={styles.creatorContainer}>
                                                    <span>👤</span>
                                                    <span>{event.createdBy}</span>
                                                </div>
                                            </td>
                                            <td className={styles.actionsCell}>
                                                <ActionMenu
                                                    event={event}
                                                    onEdit={handleEditEvent}
                                                    onDelete={handleDelete}
                                                    onWhatsAppShare={handleWhatsAppShare}
                                                    onSendInvitations={handleSendInvitations}
                                                    loading={loading}
                                                />
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="8" className={styles.noResults}>
                                            {searchTerm || statusFilter ? 'לא נמצאו אירועים התואמים לחיפוש' : 'אין אירועים'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile Cards View */}
                    <div className={styles.mobileCardsContainer}>
                        <div className={styles.cardsGrid}>
                            {currentEvents.length > 0 ? (
                                currentEvents.map((event) => (
                                    <EventCard
                                        key={event.id}
                                        event={event}
                                        onEdit={handleEditEvent}
                                        onDelete={handleDelete}
                                        onWhatsAppShare={handleWhatsAppShare}
                                        onSendInvitations={handleSendInvitations}
                                        getStatusClass={getStatusClass}
                                        loading={loading}
                                    />
                                ))
                            ) : (
                                <div className={styles.emptyState}>
                                    {searchTerm || statusFilter ? 'לא נמצאו אירועים התואמים לחיפוש' : 'אין אירועים'}
                                </div>
                            )}
                        </div>
                    </div>

                    {filteredEvents.length > 0 && (
                        <div className={styles.pagination}>
                            <div className={styles.paginationControls}>
                                <button
                                    onClick={() => paginate(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className={styles.paginationButton}
                                >
                                    <ChevronRight size={20} />
                                </button>
                                <span className={styles.pageInfo}>
                                    עמוד {currentPage} מתוך {totalPages}
                                </span>
                                <button
                                    onClick={() => paginate(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className={styles.paginationButton}
                                >
                                    <ChevronLeft size={20} />
                                </button>
                            </div>
                            <div className={styles.resultsPerPage}>
                                <span>תוצאות בעמוד:</span>
                                <select
                                    value={eventsPerPage}
                                    onChange={(e) => {
                                        setEventsPerPage(Number(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                >
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={15}>15</option>
                                    <option value={20}>20</option>
                                </select>
                            </div>
                            <div className={styles.totalInfo}>
                                מציג {indexOfFirstEvent + 1}-{Math.min(indexOfLastEvent, filteredEvents.length)} מתוך {filteredEvents.length} אירועים
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default Event;